import { useEffect } from 'react';
import type { SetRequired } from 'type-fest';
import type { ApolloError } from '@apollo/client';
import type { Dispatch } from '../../app/redux/useAppSelector';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { TaggedToastProps } from '../../toasts/toasts';
import { miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../router/useNavigate';
import handleApolloError from './handleApolloError';

interface RedirectOnErrorOptions {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  toast: TaggedToastProps;
}

const redirectOnError = ({
  dispatch,
  navigate,
  toast,
}: RedirectOnErrorOptions) => {
  dispatch(UIActions.pushToast(toast));
  navigate('/', { replace: true });
};

interface RedirectOnNotFoundErrorOptions {
  noun: string;
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
}

export const redirectOnNotFoundError = ({
  noun,
  dispatch,
  navigate,
}: RedirectOnNotFoundErrorOptions) =>
  redirectOnError({
    dispatch,
    navigate,
    toast: miscToasts.redirectOnNotFoundError({ noun }),
  });

interface UseErrorRedirectEffectOptions {
  noun: string;
  error?: ApolloError;
  forbiddenErrorToast?: TaggedToastProps;
  skip?: boolean;
}

const handleError = ({
  dispatch,
  error,
  noun,
  navigate,
  forbiddenErrorToast,
}: {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
} & SetRequired<UseErrorRedirectEffectOptions, 'error'>) => {
  handleApolloError(error, {
    NOT_FOUND: () => {
      redirectOnNotFoundError({ noun, dispatch, navigate });
    },
    FORBIDDEN: () => {
      redirectOnError({
        dispatch,
        navigate,
        toast: forbiddenErrorToast ?? miscToasts.noPermissionError({ noun }),
      });
    },
  });
};

const useErrorRedirectEffect = (options: UseErrorRedirectEffectOptions) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (options.error && !options.skip) {
      handleError({ dispatch, ...options, error: options.error, navigate });
    }
  });
};

export default useErrorRedirectEffect;
